/**
 * CRAD tab bodies that used to be placeholder text, a repeated number, or a raw JSON dump
 * (2026-10-15). Kept out of CradPage.tsx so that page stays a layout.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { craApi } from '../../lib/api';
import { LoadingSpinner, StatusBadge } from '../ui';
import type { BadgeVariant } from '../ui';

interface CradAa {
  id: string;
  name: string;
  description: string | null;
  referenceYear: number;
  stateName: string | null;
  multistateMsaName: string | null;
  counties: { fips: string; name: string }[];
  msas: { code: string; label: string }[];
  explicitTractCount: number;
  totalTracts: number;
  lmiTracts: number;
  majorityMinority: number;
  mmctMethodology: string | null;
  totalPopulation: number;
  lastRefreshedAt: string | null;
  demographics: { headers: string[]; rows: (string | number)[][]; notes: string[]; referenceYear: number } | null;
}

const pct = (v: number | null | undefined, digits = 1) =>
  v == null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(digits)}%`;
const num = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString());

function useCradAssessmentAreas(teamId: string, cradId: string) {
  return useQuery({
    queryKey: ['crad-assessment-areas', teamId, cradId],
    queryFn: () => craApi.getCradAssessmentAreas(teamId, cradId).then((r) => r.data as { evaluationYears: number[]; assessmentAreas: CradAa[] }),
    staleTime: 5 * 60 * 1000,
  });
}

function Card({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-navy-900">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

const manageLink = (
  <Link to="/cra/assessment-areas" className="text-xs font-medium text-teal-700 hover:text-teal-600">
    Manage assessment areas →
  </Link>
);

function NoAreas() {
  return (
    <p className="text-sm text-gray-500">
      No active assessment areas. Delineate them on the{' '}
      <Link to="/cra/assessment-areas" className="text-teal-700 underline">Assessment Areas</Link> page, then
      Compute CRAD.
    </p>
  );
}

// ── Assessment Areas ─────────────────────────────────────────────────────────

export function CradAssessmentAreasTab({ teamId, cradId }: { teamId: string; cradId: string }) {
  const { data, isLoading, isError } = useCradAssessmentAreas(teamId, cradId);
  return (
    <Card title="Assessment Areas" action={manageLink}>
      {isLoading ? (
        <LoadingSpinner />
      ) : isError ? (
        <p className="text-sm text-red-600">Could not load the assessment areas.</p>
      ) : !data?.assessmentAreas.length ? (
        <NoAreas />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-left text-gray-600">
                <th className="px-3 py-2 font-semibold">Assessment Area</th>
                <th className="px-3 py-2 font-semibold">Counties</th>
                <th className="px-3 py-2 font-semibold">MSA / MD</th>
                <th className="px-3 py-2 text-right font-semibold">Tracts</th>
                <th className="px-3 py-2 text-right font-semibold">LMI Tracts</th>
                <th className="px-3 py-2 text-right font-semibold">Majority-Minority</th>
                <th className="px-3 py-2 text-right font-semibold">Population</th>
              </tr>
            </thead>
            <tbody>
              {data.assessmentAreas.map((aa) => (
                <tr key={aa.id} className="border-b align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-navy-900">{aa.name}</div>
                    <div className="text-xs text-gray-500">
                      {aa.multistateMsaName ? `${aa.multistateMsaName} (multistate MSA)` : aa.stateName ?? ''}
                      {` · ${aa.referenceYear} census`}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {aa.counties.length ? aa.counties.map((c) => c.name).join(', ')
                      : aa.explicitTractCount ? `${aa.explicitTractCount} selected tracts` : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{aa.msas.length ? aa.msas.map((m) => m.label).join('; ') : 'Non-MSA'}</td>
                  <td className="px-3 py-2 text-right">{num(aa.totalTracts)}</td>
                  <td className="px-3 py-2 text-right">
                    {num(aa.lmiTracts)}
                    <span className="ml-1 text-xs text-gray-400">
                      ({aa.totalTracts ? pct(aa.lmiTracts / aa.totalTracts) : '—'})
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{num(aa.majorityMinority)}</td>
                  <td className="px-3 py-2 text-right">{num(aa.totalPopulation)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── AA Demographics ──────────────────────────────────────────────────────────

export function CradAaDemographicsTab({ teamId, cradId }: { teamId: string; cradId: string }) {
  const { data, isLoading, isError } = useCradAssessmentAreas(teamId, cradId);
  if (isLoading) return <Card title="Assessment Area Demographics"><LoadingSpinner /></Card>;
  if (isError) return <Card title="Assessment Area Demographics"><p className="text-sm text-red-600">Could not load the demographics.</p></Card>;
  if (!data?.assessmentAreas.length) return <Card title="Assessment Area Demographics"><NoAreas /></Card>;

  return (
    <div className="space-y-4">
      {data.assessmentAreas.map((aa) => (
        <Card key={aa.id} title={`Demographic Information of the Assessment Area — ${aa.name}`}>
          {!aa.demographics ? (
            <p className="text-sm text-amber-700">
              The demographic table for this assessment area could not be built. Refresh its census data on the
              Assessment Areas page.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 text-gray-600">
                      {aa.demographics.headers.map((h, i) => (
                        <th key={h} className={`px-3 py-2 font-semibold ${i === 0 ? 'text-left' : 'text-right'}`}>
                          {i >= 2 ? `${h} %` : h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {aa.demographics.rows.map((row, r) => (
                      <tr key={r} className="border-b">
                        {row.map((cell, c) => (
                          <td key={c} className={`px-3 py-1.5 ${c === 0 ? 'text-left font-medium text-navy-900' : 'text-right text-gray-700'}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Source: FFIEC census data ({aa.demographics.referenceYear}). N/A = tracts with no income classification.
                Businesses by Geography is Census CBP-derived, not Dun &amp; Bradstreet; there is no tract-level farm source.
              </p>
              {aa.demographics.notes.map((n) => (
                <p key={n} className="mt-1 text-xs text-amber-700">{n}</p>
              ))}
            </>
          )}
        </Card>
      ))}
    </div>
  );
}

// ── Loan Volume (per AA, per year) ───────────────────────────────────────────

/**
 * Small-business / small-farm loans inside each assessment area, by year. Built from the per-AA
 * Geographic Distribution results (each carries its AA's inside-AA loan count). It used to repeat
 * the institution-wide AA Concentration number.
 */
export function CradLoanVolumeTab({ results }: { results: any[] }) {
  const gd = (results ?? []).filter((r) => r.criterion === 'GEOGRAPHIC_DIST' && r.bankValue?.total != null);
  const years = [...new Set(gd.map((r) => r.dataYear as number))].sort();
  const areas = [...new Set(gd.map((r) => r.assessmentAreaName as string))].sort();
  const count = (aa: string, year: number, product: string) =>
    gd.find((r) => r.assessmentAreaName === aa && r.dataYear === year && r.productType === product)?.bankValue.total ?? 0;
  const products = [...new Set(gd.map((r) => r.productType as string))].sort();
  const label = (p: string) => (p === 'SMALL_FARM' ? 'Small Farm' : 'Small Business');

  return (
    <Card title="Loan Volume by Assessment Area and Year">
      {!gd.length ? (
        <p className="text-sm text-gray-400">No small-business / small-farm loans inside an assessment area yet. Import SB loans and compute the CRAD.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-600">
                  <th className="px-3 py-2 text-left font-semibold">Assessment Area</th>
                  {years.flatMap((y) => products.map((p) => (
                    <th key={`${y}-${p}`} className="px-3 py-2 text-right font-semibold">{y} {label(p)} #</th>
                  )))}
                </tr>
              </thead>
              <tbody>
                {areas.map((aa) => (
                  <tr key={aa} className="border-b">
                    <td className="px-3 py-2 font-medium text-navy-900">{aa}</td>
                    {years.flatMap((y) => products.map((p) => (
                      <td key={`${y}-${p}`} className="px-3 py-2 text-right">{num(count(aa, y, p))}</td>
                    )))}
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-3 py-2">Total</td>
                  {years.flatMap((y) => products.map((p) => (
                    <td key={`${y}-${p}`} className="px-3 py-2 text-right">{num(areas.reduce((s, aa) => s + count(aa, y, p), 0))}</td>
                  )))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Loans inside each assessment area from the small-business / small-farm register. HMDA volume is on the Lending Test page.
          </p>
        </>
      )}
    </Card>
  );
}

// ── AA Concentration ─────────────────────────────────────────────────────────

/** Inside vs. outside the assessment areas, by year and in total, by number and by dollar ($000s). */
export function AaConcentrationTable({ bank }: { bank: any }) {
  const years = Object.keys(bank.byYear ?? {}).map(Number).sort();
  const rows = [
    ...years.map((y) => ({ label: String(y), ...bank.byYear[y] })),
    { label: 'Total', insideCount: bank.total?.insideCount, outsideCount: bank.total?.outsideCount, insideAmt: bank.total?.insideAmt, outsideAmt: bank.total?.outsideAmt },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50 text-gray-600">
            <th className="px-3 py-2 text-left font-semibold">Year</th>
            <th className="px-3 py-2 text-right font-semibold">Inside #</th>
            <th className="px-3 py-2 text-right font-semibold">%</th>
            <th className="px-3 py-2 text-right font-semibold">Outside #</th>
            <th className="px-3 py-2 text-right font-semibold">%</th>
            <th className="px-3 py-2 text-right font-semibold">Inside $000s</th>
            <th className="px-3 py-2 text-right font-semibold">%</th>
            <th className="px-3 py-2 text-right font-semibold">Outside $000s</th>
            <th className="px-3 py-2 text-right font-semibold">%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const n = (r.insideCount ?? 0) + (r.outsideCount ?? 0);
            const d = (r.insideAmt ?? 0) + (r.outsideAmt ?? 0);
            return (
              <tr key={r.label} className={`border-b ${r.label === 'Total' ? 'bg-gray-50 font-medium' : ''}`}>
                <td className="px-3 py-2">{r.label}</td>
                <td className="px-3 py-2 text-right">{num(r.insideCount)}</td>
                <td className="px-3 py-2 text-right">{n ? pct(r.insideCount / n) : '—'}</td>
                <td className="px-3 py-2 text-right">{num(r.outsideCount)}</td>
                <td className="px-3 py-2 text-right">{n ? pct(r.outsideCount / n) : '—'}</td>
                <td className="px-3 py-2 text-right">{num(Math.round(r.insideAmt ?? 0))}</td>
                <td className="px-3 py-2 text-right">{d ? pct(r.insideAmt / d) : '—'}</td>
                <td className="px-3 py-2 text-right">{num(Math.round(r.outsideAmt ?? 0))}</td>
                <td className="px-3 py-2 text-right">{d ? pct(r.outsideAmt / d) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Geographic Distribution ──────────────────────────────────────────────────

const GD_TIERS = ['Low', 'Moderate', 'Middle', 'Upper', 'Unknown'] as const;

/** Bank loans by tract income level against the D&B and aggregate comparators, when loaded. */
export function GeographicDistTable({ bank, comparator }: { bank: any; comparator: any }) {
  const tierKey = (t: string) => `${t.toLowerCase()}Pct`;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50 text-gray-600">
            <th className="px-3 py-2 text-left font-semibold">Tract Income Level</th>
            <th className="px-3 py-2 text-right font-semibold">Bank #</th>
            <th className="px-3 py-2 text-right font-semibold">Bank %</th>
            <th className="px-3 py-2 text-right font-semibold">D&amp;B %</th>
            <th className="px-3 py-2 text-right font-semibold">Aggregate %</th>
          </tr>
        </thead>
        <tbody>
          {GD_TIERS.map((t) => (
            <tr key={t} className="border-b">
              <td className="px-3 py-2">{t}</td>
              <td className="px-3 py-2 text-right">{num(bank.distribution?.[t] ?? 0)}</td>
              <td className="px-3 py-2 text-right">{pct(bank.pcts?.[t] ?? 0)}</td>
              <td className="px-3 py-2 text-right">{t === 'Unknown' ? '—' : pct(comparator?.dnb?.[tierKey(t)])}</td>
              <td className="px-3 py-2 text-right">{t === 'Unknown' ? '—' : pct(comparator?.aggregate?.[tierKey(t)])}</td>
            </tr>
          ))}
          <tr className="bg-gray-50 font-medium">
            <td className="px-3 py-2">Total</td>
            <td className="px-3 py-2 text-right">{num(bank.total)}</td>
            <td className="px-3 py-2 text-right">100.0%</td>
            <td className="px-3 py-2" />
            <td className="px-3 py-2" />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── GD & BPD Summary ─────────────────────────────────────────────────────────

const ORDER = ['Very Poor', 'Poor', 'Reasonable', 'Excellent'];
const worst = (cs: (string | null | undefined)[]) =>
  cs.filter((c): c is string => !!c).sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b))[0] ?? null;

/** One row per assessment area (worst conclusion across years and products), then the overall row. */
export function GdBpdSummaryTable({ crad, badge }: { crad: any; badge: Record<string, BadgeVariant> }) {
  const results: any[] = crad.results ?? [];
  const areas = [...new Set(results
    .filter((r) => r.criterion === 'GEOGRAPHIC_DIST' || r.criterion === 'BORROWER_PROFILE')
    .map((r) => r.assessmentAreaName as string))].sort();
  const cell = (c: string | null) =>
    c ? <StatusBadge variant={badge[c] || 'neutral'}>{c}</StatusBadge> : <span className="text-xs text-gray-400">Not computed</span>;

  return (
    <Card title="Geographic and Borrower's Profile Distribution Summary">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50 text-left text-gray-600">
            <th className="px-3 py-2 font-semibold">Assessment Area</th>
            <th className="px-3 py-2 font-semibold">Geographic Distribution</th>
            <th className="px-3 py-2 font-semibold">Borrower's Profile</th>
          </tr>
        </thead>
        <tbody>
          {areas.map((aa) => (
            <tr key={aa} className="border-b">
              <td className="px-3 py-2 text-navy-900">{aa}</td>
              <td className="px-3 py-2">{cell(worst(results.filter((r) => r.assessmentAreaName === aa && r.criterion === 'GEOGRAPHIC_DIST').map((r) => r.conclusion)))}</td>
              <td className="px-3 py-2">{cell(worst(results.filter((r) => r.assessmentAreaName === aa && r.criterion === 'BORROWER_PROFILE').map((r) => r.conclusion)))}</td>
            </tr>
          ))}
          <tr className="bg-gray-50">
            <td className="px-3 py-2 font-medium text-navy-900">Overall</td>
            <td className="px-3 py-2">{cell(crad.geographicDistConclusion)}</td>
            <td className="px-3 py-2">{cell(crad.borrowerProfileConclusion)}</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-2 text-xs text-gray-500">Each assessment area shows its weakest conclusion across years and products.</p>
    </Card>
  );
}
